import axios, { type AxiosResponse } from 'axios';

// Define the base URL for your API
const API_URL = process.env.VUE_APP_API_URL;

if (!API_URL) {
  throw new Error('VUE_APP_API_URL environment variable is required');
}

// Create an Axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to include the token in headers
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Define types based on API documentation
export interface User {
  id?: number;
  name: string;
  surname?: string;
  email: string;
  profession?: string;
  email_verified_at?: string | null;
  // Add other user fields as needed
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  user: User;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  title: string;
  message: string;
  errors: Record<string, any>;
}

export interface RegisterData {
  name: string;
  surname: string;
  email: string;
  profession: string;
  password: string;
  password_confirmation: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface VerifyEmailData {
  email: string;
  verification_code: string;
}

export interface LastCalculationData {
  id: number;
  user_id: number;
  endpoint: string;
  input_data: any;
  output_data: any;
  external_api_url: string;
  status: string;
  status_code: number;
  error_message: string | null;
  response_time_ms: number;
  created_at: string;
  updated_at: string;
}

export interface LastCalculationResponse {
  last_calculation: LastCalculationData;
}

export interface ForgotPasswordData {
  email: string;
}

export interface ResetPasswordData {
  token: string;
  email: string;
  password: string;
  password_confirmation: string;
}

export interface PasswordResetResponse {
  title: string;
  message: string;
  status: number;
}

// Define the authentication service
const authService = {
  register: (data: RegisterData): Promise<AxiosResponse<ApiResponse<AuthResponse>>> => {
    return apiClient.post('/register', data);
  },

  login: (data: LoginData): Promise<AxiosResponse<ApiResponse<AuthResponse>>> => {
    return apiClient.post('/login', data);
  },

  logout: (): Promise<AxiosResponse<void>> => {
    return apiClient.post('/logout');
  },

  getUser: (): Promise<AxiosResponse<{ user: User }>> => {
    return apiClient.get('/user');
  },

  verifyEmail: (data: VerifyEmailData): Promise<AxiosResponse<ApiResponse<AuthResponse>>> => {
    return apiClient.post('/email/verify-code', { code: data.verification_code });
  },

  resendVerification: (email: string): Promise<AxiosResponse<ApiResponse<{ message: string }>>> => {
    return apiClient.post('/email/verification-notification');
  },

  getLastCalculation: (): Promise<AxiosResponse<ApiResponse<LastCalculationResponse>>> => {
    return apiClient.get('/calculations/last');
  },

  forgotPassword: (data: ForgotPasswordData): Promise<AxiosResponse<PasswordResetResponse>> => {
    return apiClient.post('/forgot-password', data);
  },

  resetPassword: (data: ResetPasswordData): Promise<AxiosResponse<PasswordResetResponse>> => {
    return apiClient.post('/reset-password', data);
  },
};

export default authService;
